import type { Viewing } from '@bbbk/schema';
import { Icon } from '../common';
import { StayTable, type StayDecide } from './StayTable';
import { PanelEmpty } from './PanelEmpty';
import { anomalyWord, countByReason, viewingWord, type AttentionItem } from './needsAttention';
import type { Booking } from '../bookings/types';

// The arrivals to chase (daily-command-strip AC19). Same table as In residence,
// filtered to bookings whose paperwork or deposit is outstanding — they are the
// same rows read differently, which is why one component renders both.
export interface AttentionPanelProps {
  rows: Booking[];
  items: AttentionItem[];
  onOpenBooking: (bookingId: string) => void;
  // The viewings still expected in the same window — listed AMONG the arrivals,
  // in the same table, because they are the same question read differently:
  // what needs doing before somebody turns up.
  viewings?: Viewing[];
  onOpenViewing?: (v: Viewing) => void;
  onViewAll: () => void;
  /** The capacity fact on request rows (accept-or-decline AC3). */
  decide?: StayDecide;
  // The look-ahead has not landed, or failed. TodayView maps that to empty rows,
  // and reading empty as "confirmed clear" told an operator the week was in
  // order when nothing had been checked — they could miss an arrival with
  // outstanding paperwork on the strength of it (PR #164, codex P1).
  unknown?: boolean;
}

export function AttentionPanel({ rows, items, viewings = [], onOpenBooking, onOpenViewing, onViewAll, unknown, decide }: AttentionPanelProps) {
  if (unknown) {
    return (
      <div className="cs-panel">
        <PanelEmpty
          icon="hourglass"
          line="Checking the week ahead"
          detail="The next seven days have not been read yet. Nothing here is confirmed either way until they have."
        />
      </div>
    );
  }
  // A VIEWING IS WORK, so a panel holding only viewings is not an empty panel.
  if (rows.length === 0 && viewings.length === 0) {
    return (
      <div className="cs-panel">
        <PanelEmpty
          icon="circle-check"
          line="Nothing to chase"
          detail="Every arrival in the next seven days is confirmed, with a vaccination date on file, and nobody is coming to look round. Anything that lapses will appear here the day it does."
        />
      </div>
    );
  }
  const { vaccinations, unconfirmed } = countByReason(items);
  // Only the kinds that are actually here. A "0 viewings" on a panel with none
  // is a figure the reader has to dismiss.
  // The foot reads in the segment's order: unconfirmed, vaccines, viewings.
  const summary = [
    unconfirmed > 0 && `${unconfirmed} ${anomalyWord('unconfirmed', unconfirmed)}`,
    vaccinations > 0 && `${vaccinations} ${anomalyWord('vaccinations', vaccinations)}`,
    viewings.length > 0 && `${viewings.length} ${viewingWord(viewings.length)}`,
  ].filter((s): s is string => !!s);
  return (
    <div className="cs-panel">
      <div className="cs-panel-head">
        <Icon name="alert-triangle" size={13} className="cs-panel-ico" />
        <span className="cs-panel-label">Needs attention</span>
        <button type="button" className="cs-panel-link" onClick={onViewAll}>
          Open in Bookings →
        </button>
      </div>
      <StayTable
        rows={rows}
        viewings={viewings}
        onOpenBooking={onOpenBooking}
        onOpenViewing={onOpenViewing}
        showArrivalDay
        statusFirst
        label="Arrivals needing attention"
        decide={decide}
      />
      <div className="cs-panel-foot">
        {/* THE COUNTERS' OWN WORDS, read from the same two functions they read.
            This line hand-wrote "vaccination(s)" and "awaiting a deposit" while
            the bar directly above it — same click, same countByReason data —
            said "vaccines" and "unconfirmed". Two surfaces an inch apart
            describing one set in two vocabularies, and the mismatch became
            visible far more often once the bar's words stopped being gated to
            the lone-counter case (claude, PR #171).
            The list drops any kind that is absent, so a panel with no viewings
            says nothing about viewings rather than reporting a zero the reader
            has to dismiss. */}
        <span>{summary.join(' · ')}</span>
      </div>
    </div>
  );
}
